import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { TransactionProvider, useTransactions } from '../contexts/TransactionContext';
import { TransactionForm } from '../components/transactions/TransactionForm';
import { TransactionList } from '../components/transactions/TransactionList';
import { TransactionFiltersComponent } from '../components/transactions/TransactionFilters';
import { Transaction, TransactionFilters } from '../services/transactionApi';
import { useNavigationIntent } from '../hooks/useNavigationIntent';
import { useAuth } from '../contexts/AuthContext';
import { useFinancialContexts } from '../contexts/ContextsContext';
import { formatMoneyCents } from '../theme/chart';
import { tokens } from '../theme/tokens';
import { Tile } from '../components/dashboard/home/ui';

const TransactionsPageContent: React.FC = () => {
  const { t, i18n } = useTranslation('transactions');
  const { state, loadTransactions, setFilters } = useTransactions();
  const { selectedContextId, selectedContext } = useFinancialContexts();
  const { state: authState } = useAuth();
  const [formOpen, setFormOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
  const [resetSignal, setResetSignal] = useState(0);
  const inGroup = Boolean(selectedContext && selectedContext.type !== 'personal');

  // "Registrar → Preencher aqui" lands here with the form open.
  useNavigationIntent('newTransaction', true, () => {
    setEditingTransaction(null);
    setFormOpen(true);
  });

  // The context picker at the top is global: when it changes, the list follows.
  const filtersRef = useRef(state.filters);
  filtersRef.current = state.filters;
  const lastContextRef = useRef(selectedContextId);
  useEffect(() => {
    if (lastContextRef.current === selectedContextId) return;
    lastContextRef.current = selectedContextId;
    const scoped = { ...filtersRef.current, contextId: selectedContextId, page: 1 };
    setFilters(scoped);
    loadTransactions(scoped);
  }, [selectedContextId, setFilters, loadTransactions]);

  // The selected context decides whose entries are in scope, so it travels with every filter change.
  const handleFiltersChange = (filters: TransactionFilters) => {
    const scoped = { ...filters, contextId: selectedContextId };
    setFilters(scoped);
    loadTransactions(scoped);
  };

  const openNew = () => {
    setEditingTransaction(null);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setEditingTransaction(null);
    loadTransactions(state.filters);
  };

  const currency = selectedContext?.defaultCurrency || authState.user?.defaultCurrency || 'BRL';
  const money = (amount: number) => formatMoneyCents(amount, currency, i18n.language);
  const { summary } = state;
  const net = Number(summary.netAmount) || 0;
  const scopeName = inGroup ? selectedContext?.name : t('page.personal');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, md: 2.5 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Box component='h1' sx={{ m: 0, fontFamily: tokens.display, fontSize: { xs: 26, md: 30 }, fontWeight: 700, letterSpacing: '-0.025em' }}>
            {t('title')}
          </Box>
          <Box component='span' sx={{ fontSize: 14, color: tokens.ink3 }}>
            {t('page.subtitle', { scope: scopeName, count: summary.transactionCount })}
          </Box>
        </Box>
        <Button variant='contained' startIcon={<AddIcon />} onClick={openNew} sx={{ display: { xs: 'none', md: 'inline-flex' }, minHeight: 44 }}>
          {t('page.new')}
        </Button>
      </Box>

      <Box
        component='section'
        aria-label={t('page.totals')}
        sx={{ display: 'grid', gridTemplateColumns: `repeat(${inGroup ? 2 : 3}, minmax(0, 1fr))`, gap: { xs: 1, md: 1.5 } }}
      >
        {/* a group spends together and earns nothing: its spending and how many entries, no "balance" */}
        {inGroup ? (
          <>
            <Tile label={t('summary.groupExpenses')} value={money(Number(summary.totalExpenses) || 0)} size={22} />
            <Tile label={t('summary.entries')} value={String(summary.transactionCount)} size={22} />
          </>
        ) : (
          <>
            <Tile label={t('summary.totalIncome')} value={money(Number(summary.totalIncome) || 0)} size={22} />
            <Tile label={t('summary.totalExpenses')} value={money(Number(summary.totalExpenses) || 0)} size={22} />
            <Tile label={t('summary.netAmount')} value={money(net)} tone='accent' valueColor={net < 0 ? tokens.warn : undefined} size={22} />
          </>
        )}
      </Box>

      {state.error && <Alert severity='error'>{state.error}</Alert>}

      <TransactionFiltersComponent onFiltersChange={handleFiltersChange} resetSignal={resetSignal} />

      <TransactionList
        onEditTransaction={(transaction) => {
          setEditingTransaction(transaction);
          setFormOpen(true);
        }}
        onClearFilters={() => setResetSignal((value) => value + 1)}
      />

      <TransactionForm open={formOpen} onClose={handleFormClose} transaction={editingTransaction} mode={editingTransaction ? 'edit' : 'create'} />
    </Box>
  );
};

const TransactionsPage: React.FC = () => (
  <TransactionProvider>
    <TransactionsPageContent />
  </TransactionProvider>
);

export default TransactionsPage;
